import React, { useState } from 'react';
import { Cell, flexRender, Row, VisibilityState } from '@tanstack/react-table';
import { ColumnId } from '@/experiences/sales-portal/domain/pricing/screens/management/types';
import { createStyles } from '@/contexts/theme-context';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import {
  isDeltaColumn,
  isLeftAlignedColumn,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { Pressable } from 'react-native';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';
import { useNavigation } from '@react-navigation/native';
import { VariantPackCostChange } from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';

interface PricingTableRowProps {
  /**
   * Drives memo invalidation when the user reorders columns. Read by
   * `row.getVisibleCells()` via TanStack table state, but listed here so
   * `React.memo`'s shallow compare picks up reorder changes.
   */
  columnOrder?: string[];
  /**
   * Drives memo invalidation when the user toggles column visibility. Same
   * rationale as `columnOrder` — kept in props purely to bust the memo.
   */
  columnVisibility?: VisibilityState;
  index: number;
  /**
   * Fired right before the row's default navigate-to-item-detail. Use this to
   * record state (e.g. scroll-anchor) tied to the row the user just chose.
   * Ignored when `onPress` is provided (the caller owns navigation entirely).
   */
  onBeforeNavigate?: () => void;
  onPress?: () => void;
  row: Row<VariantPackCostChange>;
}

export const PricingTableRow = React.memo<PricingTableRowProps>(
  ({ index, onBeforeNavigate, onPress, row }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [styles] = useStyles();
    const [alignmentStyles] = usePricingCellAlignmentStyles();
    const navigation =
      useNavigation<StackNavigationProp<SalesPortalStackParams>>();

    const handleHoverIn = () => setIsHovered(true);
    const handleHoverOut = () => setIsHovered(false);

    const hoveredStyle = isHovered ? styles.hovered : undefined;

    const navigateToItemManagementScreen = () => {
      onBeforeNavigate?.();
      navigation.navigate(SALES_PORTAL_ROUTES.PricingItemManagement, {
        supplierUUID: row.original.supplierUUID,
        variantPackUUID: row.original.variantPackUUID,
      });
    };

    return (
      <Pressable
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onPress={onPress ?? navigateToItemManagementScreen}
        tabIndex={-1}
        testID={`pricing-table-row-${row.index}`}
      >
        <TableRow index={index} key={row.id}>
          {row
            .getVisibleCells()
            .map((cell: Cell<VariantPackCostChange, unknown>) => {
              const columnId = cell.column.id as ColumnId;
              const alignmentStyle = getCellAlignmentStyle({
                isDelta: isDeltaColumn(columnId),
                isLeftAligned: isLeftAlignedColumn(columnId),
                styles: alignmentStyles,
              });
              return (
                <TableCell
                  cell={cell}
                  key={cell.id}
                  style={{ ...alignmentStyle, ...hoveredStyle }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
        </TableRow>
      </Pressable>
    );
  },
);

const useStyles = createStyles((theme) => ({
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
}));
